import { useState } from 'react'
import { Button, Card, IconButton, Input, Person } from '../../../design/components'
import styles from '../../scenes/sections/play.module.css'
import { useEncounterCandidates } from '../queries'
import type { EncounterSide, EncounterSidesSection } from '../types'
import type { EncounterEditing } from '../useEncounterEditing'

/**
 * The encounter's sides and who stands on each.
 *
 * There is no permission check anywhere in this card, and that is the access model rather
 * than an oversight: encounters are Game Master-only for reads as well as writes, because
 * a participant's card is an entire NPC sheet. Anyone who can see this can write it — the
 * gate is the entry point, which players never see.
 */
export function SidesCard({
  encounterId,
  sides,
  editing,
}: {
  encounterId: number
  sides: EncounterSidesSection | undefined
  editing: EncounterEditing
}) {
  const [addingSide, setAddingSide] = useState(false)
  const [sideName, setSideName] = useState('')

  if (sides === undefined) return null

  const all = sides.sides ?? []

  return (
    <Card
      variant="harm"
      label="Sides"
      hint={all.length === 1 ? '1 side' : `${all.length} sides`}
      action={
        !addingSide ? (
          <IconButton label="Add a side" icon="add" onClick={() => setAddingSide(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !addingSide && <p className={styles.empty}>No sides yet.</p>}

      {all.map((side) => (
        <SideBlock key={side.id} encounterId={encounterId} side={side} editing={editing} />
      ))}

      {addingSide && (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault()
            editing.addSide.mutate(
              { sideName },
              {
                onSuccess: () => {
                  setSideName('')
                  setAddingSide(false)
                },
              },
            )
          }}
        >
          <Input
            label="Side name"
            autoFocus
            value={sideName}
            onChange={(event) => setSideName(event.target.value)}
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setAddingSide(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={editing.addSide.isPending || sideName.trim() === ''}>
              {editing.addSide.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  )
}

function SideBlock({
  encounterId,
  side,
  editing,
}: {
  encounterId: number
  side: EncounterSide
  editing: EncounterEditing
}) {
  const [adding, setAdding] = useState(false)

  return (
    <div className={styles.side}>
      <div className={styles.sideHead}>
        <span className={styles.sideName}>{side.sideName}</span>
        <span className={styles.orderButtons}>
          <IconButton
            label={`Add someone to ${side.sideName}`}
            icon="add"
            onClick={() => setAdding(true)}
          />
          <Button
            variant="danger"
            disabled={editing.removeSide.isPending}
            onClick={() => editing.removeSide.mutate({ sideId: side.id })}
          >
            Remove
          </Button>
        </span>
      </div>

      {side.characters.length === 0 ? (
        <p className={styles.empty}>Nobody on this side.</p>
      ) : (
        <div className={styles.subList}>
          {side.characters.map((entry) => (
            <div key={entry.id} className={styles.subRow}>
              <Person
                name={entry.name}
                portraitUrl={entry.portraitUrl}
                meta={String(entry.type)}
                characterId={entry.characterId}
              />
              <Button
                variant="danger"
                disabled={editing.removeCharacter.isPending}
                onClick={() =>
                  editing.removeCharacter.mutate({
                    sideId: side.id,
                    characterId: entry.characterId,
                  })
                }
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <AddToSideForm
          encounterId={encounterId}
          side={side}
          editing={editing}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  )
}

function AddToSideForm({
  encounterId,
  side,
  editing,
  onClose,
}: {
  encounterId: number
  side: EncounterSide
  editing: EncounterEditing
  onClose: () => void
}) {
  // Server-filtered to the roster minus everyone already on *any* side — a character
  // stands on one side or none, and the API decides that, not this picker.
  const candidates = useEncounterCandidates(encounterId)
  const found = candidates.data ?? []

  return (
    <div className={styles.inlineForm}>
      {candidates.isPending && <p className={styles.note}>Loading the roster…</p>}

      {candidates.isSuccess && found.length === 0 && (
        <p className={styles.note}>Everyone on the roster is already in this encounter.</p>
      )}

      <div className={styles.pickerList}>
        {found.map((candidate) => (
          <button
            key={candidate.characterId}
            type="button"
            className={styles.pickerRow}
            disabled={editing.addCharacter.isPending}
            onClick={() =>
              editing.addCharacter.mutate(
                { sideId: side.id, characterId: candidate.characterId ?? 0 },
                { onSuccess: onClose },
              )
            }
          >
            <Person
              name={candidate.name ?? ''}
              portraitUrl={candidate.portraitUrl}
              meta={String(candidate.type ?? '')}
            />
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
